import React, { useEffect, useState } from "react";
import Profile from "./Profile"; // Import the Profile component
import GeneralSettings from "./GeneralSettings"; // Import GeneralSettings component
import PrivacySettings from "./PrivacySettings";
import FlightSearch from "./FlightSearch";
import ContactUs from "./ContactUs";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import { useNavigate, useSearchParams } from "react-router-dom";

const Index: React.FC = () => {
  const { showLoader, hideLoader } = useGlobalLoader();
  const [searchParams, setSearchParams] = useSearchParams();
  const getInitialSection = () => {
    const section = searchParams.get("section");
    return section ?? "profile";
  };
  const [activeComponent, setActiveComponent] = useState<string>(getInitialSection()); // State to track active component
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate(); // Initialize the navigate function

  useEffect(() => {
    showLoader();
    setTimeout(() => {
      hideLoader();
    }, 1000); // Shows loader for 1 second on initial mount
  }, []);

  const menuItems = [
    { id: "profile", label: "Profile", icon: "st-user" },
    { id: "privacy", label: "Privacy", icon: "st-shield" },
    // { id: "general", label: "General", icon: "st-info" },
    { id: "flightSearch", label: "Flight Search", icon: "st-flight" },
    { id: "contactUs", label: "Contact Us", icon: "st-chat" },
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleMenuItemClick = (id: string) => {
    setActiveComponent(id); // Update active component
    if (id === "profile") {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ section: id }, { replace: true });
    }
    setIsSidebarOpen(false); // Close the sidebar when a menu item is clicked
  };

  useEffect(() => {
    const section = searchParams.get("section");
    const allowableSections = ["profile", "privacy", "flightSearch", "contactUs"];

    if (section && allowableSections.includes(section) && section !== activeComponent) {
      setActiveComponent(section);
    } else if (!section && activeComponent !== "profile") {
      setActiveComponent("profile");
    }
  }, [searchParams, activeComponent]);

  return (
    <div className="">
      {/* Header Section */}
      <section className="flex justify-between p-8 h-[125.5px] bg-cover">
        <div className="flex">
          <p className="text-black text-lg font-semibold sm:ml-0 ml-4">Settings</p>
        </div>
        <div className="flex items-center gap-2 -mt-6">
          <div
            className="flex items-center gap-2 p-2"
            onClick={() => navigate("/search")}
          >
            <i className="st-home text-[#262626] text-xs"></i>
            <span className="text-[#262626] text-sm font-extralight cursor-pointer">
              Home
            </span>
            <i className="st-arrow-right text-[#262626] text-xs"></i>
          </div>
          <p className="text-[#262626] text-sm font-bold">Settings</p>
        </div>
      </section>

      {/* Sidebar Section */}
      <section className="relative sm:top-0 -top-[107px]">
        <button
          onClick={toggleSidebar}
          aria-controls="default-sidebar"
          type="button"
          className="inline-flex items-center py-2 mt-2 ml-4 text-sm text-gray-500 rounded-lg sm:hidden focus:outline-none"
        >
          <span className="sr-only">Open sidebar</span>
          <svg
            className="w-6 h-6"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              fillRule="evenodd"
              d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
            ></path>
          </svg>
        </button>

        {/* Backdrop for Mobile (only visible on mobile) */}
        {isSidebarOpen && (
          <div
            className="bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-30 sm:hidden" // This ensures it's hidden on screens larger than 'sm'
            onClick={toggleSidebar} // Close sidebar when clicking on backdrop
          ></div>
        )}

        {/* Sidebar Menu */}
        <div className="relative mx-5 h-screen">
          <div className="absolute sm:-top-12 md:-top-16 w-full mx-auto">
            <aside
              id="default-sidebar"
              className={`sm:absolute fixed bg-white sm:rounded-xl z-40 w-60 h-full transition-transform left-0 top-4 transform-none 
            ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} sm:translate-x-0`}
              aria-label="Sidebar"
            >
              <div className="h-full p-6 overflow-y-auto">
                <ul className="space-y-2">
                  {menuItems.map((item) => (
                    <li
                      key={item.id}
                      className={`rounded-lg px-2 ${activeComponent === item.id ? "bg-[#FFBC001A]" : ""}`}
                    >
                      <a
                        href="#"
                        className={`flex items-center p-2 ${activeComponent === item.id ? "text-[#FFD700]" : "text-grease-100"}`}
                        onClick={() => handleMenuItemClick(item.id)} // Handle menu item click to close the sidebar
                      >
                        <i
                          className={`${item.icon} ${activeComponent === item.id ? "text-[#FFD700]" : "text-lightgray-150"} text-lg`}
                        ></i>
                        <span className="ms-3 text-sm">{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main Content Section */}
            <div className="">
              {/* Conditionally render the active component */}
              {activeComponent === "profile" && <Profile />}
              {activeComponent === "privacy" && <PrivacySettings />}
              {activeComponent === "general" && <GeneralSettings />}
              {activeComponent === "flightSearch" && <FlightSearch />}
              {activeComponent === "contactUs" && <ContactUs />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
